<template>
  <input
    ref="input"
    type="tel"
    class="v-money"
    v-bind="attributes"
    :value="formattedValue"
    @input="onInput"
    @focus="onFocus"
  />
</template>

<script>
/**
 * <money v-model="price">: the panel's money field, behaving exactly like the
 * v-money 0.8.1 component it replaces (that library is Vue 2 only, and v-money3
 * changes what the model receives).
 *
 * What the forms rely on:
 * - shows the value formatted ("1,234.50"), and reformats while typing, keeping
 *   the cursor where it was counted from the end, never inside the prefix;
 * - the model gets a Number (masked: false) or the formatted text (masked: true)
 *   on every keystroke, and once when the field appears (so "12.5" from the API
 *   becomes 12.5, as before);
 * - focusing puts the cursor before the suffix;
 * - attributes (class, id, type, size, disabled, ...) land on the input, the
 *   parent's winning over the defaults, as they did;
 * - listeners do not: v-money emitted only "input" (the model), so
 *   @change/@blur/@keyup written on <money> (SizeGroup, InvoiceForm) never ran.
 *   Vue 3 would attach them to the input and start running them, which would
 *   change when those costs are calculated. "input" is still emitted with the
 *   value, for any @input written on <money>.
 * The arithmetic below is v-money's, kept as it was.
 */
const DEFAULTS = { prefix: "", suffix: "", thousands: ",", decimal: ".", precision: 2 };

function between(min, n, max) {
  return Math.max(min, Math.min(n, max));
}

function fixed(precision) {
  return between(0, precision, 20);
}

function onlyNumbers(input) {
  return (input ? input.toString() : "").replace(/\D+/g, "") || "0";
}

function numbersToCurrency(numbers, precision) {
  const exp = Math.pow(10, precision);
  return (parseFloat(numbers) / exp).toFixed(fixed(precision));
}

function addThousandSeparator(integer, separator) {
  return integer.replace(/(\d)(?=(?:\d{3})+\b)/gm, "$1" + separator);
}

export function format(input, opt = DEFAULTS) {
  if (input == null) input = 0; // v-money crashed here; its end state was 0.00
  if (typeof input === "number") input = input.toFixed(fixed(opt.precision));
  const negative = input.indexOf("-") >= 0 ? "-" : "";
  const currency = numbersToCurrency(onlyNumbers(input), opt.precision);
  const [integer, decimal] = currency.split(".");
  const joined = decimal ? addThousandSeparator(integer, opt.thousands) + opt.decimal + decimal : addThousandSeparator(integer, opt.thousands);
  return opt.prefix + negative + joined + opt.suffix;
}

export function unformat(input, precision) {
  const negative = input.indexOf("-") >= 0 ? -1 : 1;
  return parseFloat(numbersToCurrency(onlyNumbers(input), precision)) * negative;
}

function setCursor(el, position) {
  const set = () => el.setSelectionRange(position, position);
  if (el === document.activeElement) {
    set();
    setTimeout(set, 1);
  }
}

export default {
  name: "MoneyInput",
  inheritAttrs: false,
  props: {
    modelValue: { type: [Number, String], default: 0 },
    masked: { type: Boolean, default: false },
    precision: { type: Number, default: DEFAULTS.precision },
    decimal: { type: String, default: DEFAULTS.decimal },
    thousands: { type: String, default: DEFAULTS.thousands },
    prefix: { type: String, default: DEFAULTS.prefix },
    suffix: { type: String, default: DEFAULTS.suffix },
  },
  emits: ["update:modelValue", "input"],
  data() {
    return { formattedValue: "" };
  },
  computed: {
    /** The parent's attributes without its listeners (see above). */
    attributes() {
      const out = {};
      for (const [key, value] of Object.entries(this.$attrs)) if (!/^on[A-Z]/.test(key)) out[key] = value;
      return out;
    },
    options() {
      return { precision: this.precision, decimal: this.decimal, thousands: this.thousands, prefix: this.prefix, suffix: this.suffix };
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        const formatted = format(value, this.options);
        if (formatted !== this.formattedValue) this.formattedValue = formatted;
      },
    },
  },
  mounted() {
    // v-money formatted the field as soon as it existed and reported the value.
    this.onInput();
  },
  methods: {
    onInput() {
      const el = this.$refs.input;
      let position = el.value.length - el.selectionEnd;
      el.value = format(el.value, this.options);
      position = Math.max(position, this.suffix.length);
      position = el.value.length - position;
      position = Math.max(position, this.prefix.length + 1);
      setCursor(el, position);
      const value = this.masked ? el.value : unformat(el.value, this.precision);
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },
    onFocus() {
      const el = this.$refs.input;
      setCursor(el, el.value.length - this.suffix.length);
    },
  },
};
</script>
